import React, { useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { ItemType } from './constants';
import EditReportNameModal from './modals/EditReportNameModal';
import FolderManagerModal from './modals/FolderManagerModal';
import { useTranslation } from 'react-i18next';
import './styles/customAccordion.css';

const ReportsList = ({ 
  reports, 
  folders, 
  onSelectReport, 
  onDeleteReport, 
  onCreateNewReport, 
  onUpdateReportName, 
  onMoveReportToFolder, 
  onRenameFolder,
  onSaveFolder,
  onCreateFolder,
  onDeleteFolder,
 }) => {
  const { t } = useTranslation();
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [activeReportId, setActiveReportId] = useState(null);
  const [openFolderId, setOpenFolderId] = useState(null);
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleEditReport = (report) => {
    setSelectedReport(report);
    setShowEditModal(true);
  };

  const handleSaveReport = (updatedReport) => {
    onUpdateReportName(updatedReport);
    setShowEditModal(false);
  };

  const handleSelectReport = (report) => {
    onSelectReport(report.report_id, report.name);
    setActiveReportId(report.report_id);
  };

  const handleRenameFolder = (folder) => {
    onRenameFolder(folder);
  };

  const handleOpenAddFolderModal = () => {
    setShowAddFolderModal(true);
  };

  const handleCloseAddFolderModal = () => {
    setShowAddFolderModal(false);
  };

  const sortedFolders = [...folders].sort((a, b) => {
    if (a.name === 'General') return -1; // General na prvé miesto
    if (b.name === 'General') return 1;
  
    const yearA = parseInt(a.name.split('_')[1], 10); // Extrakcia roka z názvu
    const yearB = parseInt(b.name.split('_')[1], 10);
  
    // Najskôr podľa roka (väčší rok ide skôr)
    if (yearA !== yearB) return yearB - yearA;
  
    // Ak sú roky rovnaké, zoradiť podľa mesiaca (väčší mesiac ide skôr)
    const monthA = parseInt(a.name.split('_')[0].split(' ')[1], 10);
    const monthB = parseInt(b.name.split('_')[0].split(' ')[1], 10);
    return monthB - monthA;
  });

  const filteredFolders = sortedFolders.map(folder => {
    const reportsInFolder = reports.filter(
      report =>
        report.folder_id === folder.folder_id &&
        report.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const folderMatches = folder.name.toLowerCase().includes(searchTerm.toLowerCase());

    if (folderMatches || reportsInFolder.length > 0) {
      return {
        ...folder,
        filteredReports: reportsInFolder
      };
    }

    return null;
  }).filter(Boolean);

  return (
    <div className="reports-list">
      <div className="reports-list-actions">
        <button
          className="reports-create-button flwrk-cta"
          type="button"
          onClick={onCreateNewReport}
          disabled={folders.length === 0}
        >
          <i className="bi bi-file-earmark-plus" aria-hidden="true"></i>
          <span>{t('new_report')}</span>
        </button>
        <button
          className="reports-folders-button"
          type="button"
          onClick={handleOpenAddFolderModal}
          aria-label={t('manage_folders')}
          title={t('manage_folders')}
        >
          <i className="bi bi-folder2-open" aria-hidden="true"></i>
        </button>
      </div>

      <div className="reports-list-search">
        <i className="bi bi-search" aria-hidden="true"></i>
        <input
          type="search"
          className="form-control"
          placeholder={t('search_reports_folders')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className="reports-list-search-clear"
            type="button"
            onClick={() => setSearchTerm('')}
            aria-label={t('clear_search')}
            title={t('clear_search')}
          >
            <i className="bi bi-x" aria-hidden="true"></i>
          </button>
        )}
      </div>

      <div className="accordion reports-list-tree" id="accordionFolders">
        {filteredFolders.map(folder => (
          <Folder
            key={folder.folder_id}
            folder={folder}
            reports={folder.filteredReports}
            onMoveReportToFolder={onMoveReportToFolder}
            handleSelectReport={handleSelectReport}
            handleEditReport={handleEditReport}
            handleDeleteReport={onDeleteReport}
            handleRenameFolder={handleRenameFolder}
            activeReportId={activeReportId}
            isOpen={openFolderId === folder.folder_id}
            onToggle={() => setOpenFolderId((currentFolderId) => (
              currentFolderId === folder.folder_id ? null : folder.folder_id
            ))}
          />
        ))}
      </div>
      <EditReportNameModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        report={selectedReport}
        onSave={handleSaveReport}
      />
      <FolderManagerModal
        show={showAddFolderModal}
        handleClose={handleCloseAddFolderModal}
        folders={folders}
        onCreateFolder={onCreateFolder}
        onRenameFolder={onSaveFolder}
        onDeleteFolder={onDeleteFolder}
      />
    </div>
  );
};

const Folder = ({ folder, reports, onMoveReportToFolder, handleSelectReport, handleEditReport, handleDeleteReport, activeReportId, isOpen, onToggle }) => {
  const { t } = useTranslation();
  const [, drop] = useDrop({
    accept: ItemType.REPORT,
    drop: (item) => {
      onMoveReportToFolder(item.report_id, folder.folder_id, folder.name);
    },
  });

  return (
    <div ref={drop} className="accordion-item accordion-folder-fs-small">
      <h2 className="accordion-header" id={`heading-${folder.folder_id}`}>
        <button className={`accordion-button btn btn-light mx-0 mb-0 ${isOpen ? "" : "collapsed"}`} type="button" onClick={onToggle} aria-expanded={isOpen} aria-controls={`collapse-${folder.folder_id}`}>
          <i className="bi bi-folder px-2"></i>{folder.name}
        </button>
      </h2>
      <div id={`collapse-${folder.folder_id}`} className={`accordion-collapse collapse ${isOpen ? "show" : ""}`} aria-labelledby={`heading-${folder.folder_id}`}>
        <div className="accordion-body px-0 py-0">
          <ul className='pt-1 mb-0'>
          {reports.length === 0 ? (
            <p className='py-125 mb-0'>{t('no_reports')}</p>
          ) : (
            reports.map(report => (
              <Report
                key={report.report_id}
                report={report}
                onSelectReport={handleSelectReport}
                onDeleteReport={handleDeleteReport}
                onEditReport={handleEditReport}
                activeReportId={activeReportId}
              />
            ))
          )}
          </ul>
        </div>
      </div>
    </div>
  );
};

const Report = ({ report, onSelectReport, onDeleteReport, onEditReport, activeReportId }) => {
  const [, drag] = useDrag({
    type: ItemType.REPORT,
    item: { report_id: report.report_id },
  });

  return (
    <li className='mb-0 w-100' ref={drag}>
      <div className={`button-wrapper accordion-report-fs-small d-flex justify-content-between align-items-center ${activeReportId === report.report_id ? 'active' : ''}`}>
        <button className={`btn btn-link ${activeReportId === report.report_id ? 'fw-600' : ''}`} onClick={() => onSelectReport(report)}>
          <i className="bi bi-grip-vertical px-2"></i>
          {report.name || `Report from ${new Date(report.created_at).toLocaleDateString()}`}
        </button>
        <span>
          <button
            type="button"
            className='btn btn-light btn-icon mb-0 mx-1'
            onClick={() => onEditReport(report)}
            data-bs-toggle="tooltip"
            data-bs-placement="right"
            title="Edit"
          ><i className="bi bi-pencil"></i>
          </button>
          <button
            type="button"
            className='btn btn-light btn-icon mb-0 ms-0'
            onClick={() => onDeleteReport(report.report_id)}
            data-bs-toggle="tooltip"
            data-bs-placement="right"
            title="Remove"
          ><i className="bi bi-trash"></i>
          </button>
        </span>
      </div>
    </li>
  );
};

export default ReportsList;
